import { parsePhoneNumber, isValidPhoneNumber } from "libphonenumber-js";

/**
 * Validates phone number input and formats it
 * @param {string} selector - The input field selector
 * @param {string} defaultCountry - Default country code (e.g., 'CH' for Switzerland)
 */
export function initPhoneNumberValidation(selector, defaultCountry = "CH") {
	const phoneInput = document.querySelector(selector);
	if (!phoneInput) return;

	// Cache helpers
	const CACHE_KEY = "country_code";
	const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours

	const getValueFromCache = async (key) => {
		const item = localStorage.getItem(key);
		if (!item) return null;

		const { value, timestamp } = JSON.parse(item);
		if (Date.now() - timestamp > CACHE_DURATION) {
			localStorage.removeItem(key);
			return null;
		}
		return value;
	};

	const saveValueInCache = (key, value) => {
		const item = {
			value,
			timestamp: Date.now()
		};
		localStorage.setItem(key, JSON.stringify(item));
	};

	// Error handling helpers
	const createErrorDiv = (input) => {
		const fieldContainer = input.closest(".form-group");
		let errorDiv = fieldContainer.querySelector(".error-message");
		if (!errorDiv) {
			errorDiv = document.createElement("div");
			errorDiv.className = "error-message help-block";
			fieldContainer.appendChild(errorDiv);
		}
		return errorDiv;
	};

	const showError = (input, message) => {
		const errorDiv = createErrorDiv(input);
		errorDiv.textContent = message;
		input.closest(".form-group").classList.add("error");
		input.setAttribute("aria-invalid", "true");
	};

	const clearError = (input) => {
		const fieldContainer = input.closest(".form-group");
		const errorDiv = fieldContainer.querySelector(".error-message");
		if (errorDiv) {
			errorDiv.textContent = "";
		}
		fieldContainer.classList.remove("error");
		input.setAttribute("aria-invalid", "false");
	};

	// Country detection and initialization
	const initializeCountry = async () => {
		try {
			// Try to get country code from cache first
			let countryCode = await getValueFromCache(CACHE_KEY);

			if (!countryCode) {
				// If not in cache, fetch from API
				const response = await fetch("https://ipapi.co/json");
				const data = await response.json();
				countryCode = data.country_code.toLowerCase();

				// Save to cache
				saveValueInCache(CACHE_KEY, countryCode);
			}

			return countryCode.toUpperCase();
		} catch (error) {
			console.warn("Could not detect country:", error);
			return defaultCountry;
		}
	};

	// Initialize with detected country
	initializeCountry().then(detectedCountry => {
		defaultCountry = detectedCountry;
	});

	// Event listeners
	phoneInput.addEventListener("blur", function(e) {
		try {
			const phoneNumber = parsePhoneNumber(e.target.value, defaultCountry);
			if (phoneNumber && phoneNumber.isValid()) {
				e.target.value = phoneNumber.formatInternational();
				clearError(e.target);
			} else {
				showError(e.target, `Please enter a valid phone number. (ex.: +${defaultCountry === "CH" ? "41" : "1"} xx xxx xx xx)`);
			}
		} catch (error) {
			showError(e.target, `Please enter a valid phone number. (ex.: +${defaultCountry === "CH" ? "41" : "1"} xx xxx xx xx)`);
		}
	});

	phoneInput.addEventListener("input", function(e) {
		console.log(defaultCountry);
		try {
			if (e.target.value) {
				const isValid = isValidPhoneNumber(e.target.value, defaultCountry);
				if (isValid) {
					clearError(e.target);
				} else {
					showError(e.target, `Please enter a valid phone number. (ex.: +${defaultCountry === "CH" ? "41" : "1"} xx xxx xx xx)`);
				}
			} else {
				clearError(e.target);
			}
		} catch (error) {
			showError(e.target, "Please enter a valid phone number");
		}
	});

	// Handle form submission
	const form = phoneInput.closest("form");
	if (form) {
		form.addEventListener("submit", function(e) {
			try {
				if (phoneInput.value) {
					const isValid = isValidPhoneNumber(phoneInput.value, defaultCountry);
					if (!isValid) {
						e.preventDefault();
						showError(phoneInput, "Please enter a valid phone number");
						phoneInput.focus();
					}
				}
			} catch (error) {
				e.preventDefault();
				showError(phoneInput, "Please enter a valid phone number");
				phoneInput.focus();
			}
		});
	}
}
